import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Paper,
  Container
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../context/SessionContext';
import { useAuth } from '../../context/AuthContext';
import { playersService, expensesService } from '../../services/supabaseService';
import { isSupabaseConfigured } from '../../lib/supabase';
import MasterPlayerList from '../../components/MasterPlayerList/MasterPlayerList';
import Footer from '../../components/Footer/Footer';
import './SessionLanding.scss';

const SessionLanding = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { 
    sessions, 
    createSession, 
    updateSession, 
    deleteSession, 
    archiveSession, 
    restoreSession,
    setCurrentSession,
    getActiveSessions,
    getArchivedSessions,
    generateSessionName 
  } = useSession();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    gameType: 'Texas Hold\'em',
    stakes: '',
    location: ''
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      gameType: 'Texas Hold\'em',
      stakes: '',
      location: ''
    });
  };

  const handleCreateSession = async () => {
    try {
      const sessionName = formData.name.trim() || generateSessionName();
      const sessionId = await createSession({
        ...formData,
        name: sessionName
      });
      
      setCreateDialogOpen(false);
      resetForm();
      
      // Navigate to the new session
      setCurrentSession(sessionId);
      navigate(`/session/${sessionId}`);
    } catch (error) {
      console.error('Error creating session:', error);
      alert('Failed to create session. Please try again.');
    }
  };

  const handleEditSession = () => {
    if (selectedSession) {
      updateSession(selectedSession.id, formData);
      setEditDialogOpen(false);
      setSelectedSession(null);
      resetForm();
    }
  };

  const handleDeleteSession = () => {
    if (selectedSession) {
      deleteSession(selectedSession.id);
      setDeleteDialogOpen(false);
      setSelectedSession(null);
    }
  };

  const handleStartSession = (session) => {
    setCurrentSession(session.id);
    navigate(`/session/${session.id}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleArchiveToggle = (session) => {
    if (session.isActive) {
      archiveSession(session.id);
    } else {
      restoreSession(session.id);
    }
  };

  const openEditDialog = (session) => {
    setSelectedSession(session);
    setFormData({
      name: session.name,
      description: session.description || '',
      gameType: session.gameType || 'Texas Hold\'em',
      stakes: session.stakes || '',
      location: session.location || ''
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (session) => {
    setSelectedSession(session);
    setDeleteDialogOpen(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const activeSessions = getActiveSessions();
  const archivedSessions = getArchivedSessions();
  const displaySessions = showArchived ? archivedSessions : activeSessions;

  // State to track which sessions have data
  const [sessionsWithData, setSessionsWithData] = useState(new Set());
  const useSupabase = isSupabaseConfigured();

  // Check if a session has any data (players or expenses)
  useEffect(() => {
    const checkSessionsData = async () => {
      const sessionsWithDataSet = new Set();
      
      for (const session of displaySessions) {
        let hasData = false;
        
        if (useSupabase) {
          // Check Supabase
          try {
            const [players, expenses] = await Promise.all([
              playersService.getBySession(session.id),
              expensesService.getBySession(session.id)
            ]);
            
            if ((Array.isArray(players) && players.length > 0) || 
                (Array.isArray(expenses) && expenses.length > 0)) {
              hasData = true;
            }
          } catch (error) {
            console.error(`Error checking data for session ${session.id}:`, error);
            // Fall back to localStorage check
            hasData = checkLocalStorage(session.id);
          }
        } else {
          // Check localStorage
          hasData = checkLocalStorage(session.id);
        }
        
        if (hasData) {
          sessionsWithDataSet.add(session.id);
        }
      }
      
      setSessionsWithData(sessionsWithDataSet);
    };
    
    const checkLocalStorage = (sessionId) => {
      // Check for players
    const storedPlayers = localStorage.getItem(`pokerPlayers_${sessionId}`);
      if (storedPlayers) {
    try {
      const players = JSON.parse(storedPlayers);
          if (Array.isArray(players) && players.length > 0) {
            return true;
          }
        } catch (error) {
          // Continue to check expenses
        }
      }
      
      // Check for expenses
      const storedExpenses = localStorage.getItem(`miscExpenses_${sessionId}`);
      if (storedExpenses) {
        try {
          const expenses = JSON.parse(storedExpenses);
          if (Array.isArray(expenses) && expenses.length > 0) {
            return true;
          }
    } catch (error) {
          // Return false if both checks fail
        }
      }
      
      return false;
    };
    
    if (displaySessions.length > 0) {
      checkSessionsData();
    }
  }, [displaySessions, useSupabase]);

  // Check if a session has any data (players or expenses)
  const sessionHasPlayers = (sessionId) => {
    return sessionsWithData.has(sessionId);
  };


  return (
    <>
    <Container maxWidth="lg" className="session-landing fade-in">

      {/* User Info and Logout */}
      {user && (
        <Box className="user-header">
          <Typography variant="body2" color="text.secondary">
            Welcome, {user.name || user.email}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            className="logout-button"
            size="small"
          >
            Logout
          </Button>
        </Box>
      )}

      {/* Hero Section */}
      <Box className="landing-hero">
        <div className="hero-icon">♠</div>
        <Typography variant="h2" className="hero-title">
          Home Game
        </Typography>
        <Typography variant="h6" color="text.secondary" className="hero-subtitle">
          Organize and track multiple poker sessions with ease
        </Typography>
        <Box className="hero-actions">
          <Button
            variant="outlined"
            startIcon={<EmojiEventsIcon />}
            onClick={() => navigate('/player-stats')}
            className="stats-button"
            size="large"
          >
            Player Statistics
          </Button>
        </Box>
      </Box>

      {/* Action Bar */}
      <Box className="action-bar">
        <Box className="section-header">
          <Typography variant="h4" className="section-title">
            {showArchived ? 'Archived Sessions' : 'Active Sessions'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {displaySessions.length} {displaySessions.length === 1 ? 'session' : 'sessions'}
          </Typography>
        </Box>
        
        <Box className="header-actions">
          <Button
            variant="outlined"
            onClick={() => setShowArchived(!showArchived)}
            className="toggle-button"
          >
            {showArchived ? 'Show Active' : 'Show Archived'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            className="create-button"
          >
            New Session
          </Button>
        </Box>
      </Box>

      {/* Sessions Grid */}
      {displaySessions.length === 0 ? (
        <Paper className="empty-state">
          <div className="empty-icon">♠</div>
          <Typography variant="h5" className="empty-title">
            {showArchived ? 'No archived sessions yet' : 'No active sessions yet'}
          </Typography>
          <Typography variant="body1" color="text.secondary" className="empty-subtitle">
            {showArchived 
              ? 'Archived sessions will appear here when you archive them'
              : 'Create your first poker session to get started tracking your games'
            }
          </Typography>
          {!showArchived && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              className="empty-action-button"
            >
              Create First Session
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3} className="sessions-grid">
          {displaySessions.map((session) => (
            <Grid item xs={12} sm={6} md={4} key={session.id}>
              <Card className={`session-card ${!session.isActive ? 'archived' : ''}`}>
                <CardContent className="session-content">
                  <Box className="session-header">
                    <Typography variant="h6" className="session-name">
                      {session.name}
                    </Typography>
                    <Box className="session-actions">
                      <IconButton 
                        size="small" 
                        onClick={() => openEditDialog(session)}
                        className="edit-button"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleArchiveToggle(session)}
                        className="archive-button"
                      >
                        {session.isActive ? <ArchiveIcon fontSize="small" /> : <UnarchiveIcon fontSize="small" />}
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => openDeleteDialog(session)}
                        className="delete-button"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {session.description && (
                    <Typography variant="body2" color="text.secondary" className="session-description">
                      {session.description}
                    </Typography>
                  )}

                  <Box className="session-details">
                    <Box className="detail-item">
                      <CalendarTodayIcon className="detail-icon" />
                      <Typography variant="body2">
                        {formatDate(session.createdAt)}
                      </Typography>
                    </Box>
                    
                    {session.location && (
                      <Box className="detail-item">
                        <LocationOnIcon className="detail-icon" />
                        <Typography variant="body2">
                          {session.location}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box className="session-meta">
                    <Chip 
                      label={session.gameType} 
                      size="small" 
                      className="game-chip"
                    />
                    {session.stakes && (
                      <Chip 
                        label={session.stakes} 
                        size="small" 
                        variant="outlined"
                        className="stakes-chip"
                      />
                    )}
                    {!session.isActive && (
                      <Chip 
                        label="Archived" 
                        size="small" 
                        color="default"
                        className="archived-chip"
                      />
                    )}
                  </Box>

                  {session.isActive && (
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<PlayArrowIcon />}
                      onClick={() => handleStartSession(session)}
                      className={sessionHasPlayers(session.id) ? 'view-session-button' : 'start-session-button'}
                    >
                      {sessionHasPlayers(session.id) ? 'View Session' : 'Start Session'}
                    </Button>
                  )}
                  
                  {!session.isActive && (
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<PlayArrowIcon />}
                      onClick={() => handleStartSession(session)}
                      className="view-button"
                    >
                      View Session
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Master Player List Section */}
      <MasterPlayerList />

      {/* Create Session Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Poker Session</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Session Name (optional)"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={generateSessionName()}
            className="form-field"
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="form-field"
          />
          <TextField
            margin="dense"
            label="Game Type"
            fullWidth
            variant="outlined"
            value={formData.gameType}
            onChange={(e) => setFormData({ ...formData, gameType: e.target.value })}
            className="form-field"
          />
          <TextField
            margin="dense"
            label="Stakes (optional)"
            fullWidth
            variant="outlined"
            value={formData.stakes}
            onChange={(e) => setFormData({ ...formData, stakes: e.target.value })}
            placeholder="e.g., $1/$2, $5/$10"
            className="form-field"
          />
          <TextField
            margin="dense"
            label="Location (optional)"
            fullWidth
            variant="outlined"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            placeholder="e.g., Home Game, Casino Name"
            className="form-field"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreateSession} variant="contained">
            Create Session
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Session Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Session</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Session Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="form-field"
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="form-field"
          />
          <TextField
            margin="dense"
            label="Game Type"
            fullWidth
            variant="outlined"
            value={formData.gameType}
            onChange={(e) => setFormData({ ...formData, gameType: e.target.value })}
            className="form-field"
          />
          <TextField
            margin="dense"
            label="Stakes"
            fullWidth
            variant="outlined"
            value={formData.stakes}
            onChange={(e) => setFormData({ ...formData, stakes: e.target.value })}
            className="form-field"
          />
          <TextField
            margin="dense"
            label="Location"
            fullWidth
            variant="outlined"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="form-field"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleEditSession} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedSession?.name}"? This action cannot be undone and will permanently remove all associated data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeleteSession} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    <Footer />
    </>
  );
};

export default SessionLanding;
